import { call, put, select, take, fork } from 'redux-saga/effects';
import { waitForSaga } from '../helpers';
import { getWebsocketChannels, getWebsocketReadyState, getWebsocketReadyStateNum } from '../websocketSelectors';
import {
    websocketChannelClose,
    websocketChannelOpen,
    websocketChannelReadyStateChange,
    websocketChannelSend,
    websocketChannelSended,
    websocketChannelUpdated,
    websocketEventClose,
    websocketEventError,
    websocketEventMessage,
    websocketEventOpen,
} from '../websocketReducer';
import { END, eventChannel } from 'redux-saga';
import { getIsAppVisibility } from '../../../app/appSelectors';
import { setLiteTradeSocketState } from '../../../modules/LiteTrade/liteTradeActions';
import { subscriptionPingSubscribeSagas } from './subscribers/subscriptionPingSubscribeSagas';
import { IMobileAppRootState } from '../../../mobileAppCore/types/mobileAppState.t';

// нужно мгновенное пониммание того что новый канал УЖЕ открыт
// текущая реализация ассинхронная и пока, без костыля в виде прямого обращения не понять что канал открыт
const globalWebsocketChannels: { [key: string]: WebSocket } = {};

export function createWebsocketChannel(websocket: WebSocket, channel: string) {
    return eventChannel((emitter) => {
        const onMessage = (event: MessageEvent) => {
            emitter({ event, channel, type: 'message' });
        };
        const onOpen = (event: Event) => {
            emitter({ event, channel, type: 'open' });
        };

        const onClose = (event: Event) => {
            emitter({ event, channel, type: 'close' });
            emitter(END);
        };

        const onError = (event: Event) => {
            emitter({ event, channel, type: 'error' });
        };

        websocket.addEventListener('message', onMessage);
        websocket.addEventListener('open', onOpen);
        websocket.addEventListener('close', onClose);
        websocket.addEventListener('error', onError);
        return () => {
            websocket.removeEventListener('message', onMessage);
            websocket.removeEventListener('open', onOpen);
            websocket.removeEventListener('close', onClose);
            websocket.removeEventListener('error', onError);
            websocket.close();
        };
    });
}

// продписка на события вебсокета по "феншую" и без костылей
// при большой сложости перепишу
export function* websocketEventListenersSaga(websocket: WebSocket, channel: string) {
    try {
        const channelSaga = yield call(createWebsocketChannel, websocket, channel);
        while (websocket.readyState === WebSocket.OPEN || websocket.readyState === WebSocket.CONNECTING) {
            const { event, channel, type } = yield take(channelSaga);
            switch (type) {
                case 'message':
                    yield put(websocketEventMessage({ event, channel }));
                    break;
                case 'open':
                    yield put(websocketEventOpen({ event, channel }));
                    break;
                case 'close':
                    yield put(websocketEventClose({ event, channel }));
                    break;
                case 'error':
                    yield put(websocketEventError({ event, channel }));
                    break;
                default:
                    break;
            }
        }
        channelSaga.close();
    } catch (error) {
        console.error('websocketEventListenersSaga', error);
    }
}

export function* onOpenChannelSaga(action: ReturnType<typeof websocketChannelOpen>) {
    const { channel, ping } = action.payload;
    const websocketChannels = globalWebsocketChannels;
    if (
        !websocketChannels[channel] ||
        websocketChannels[channel].readyState === WebSocket.CLOSING ||
        websocketChannels[channel].readyState === WebSocket.CLOSED
    ) {
        try {
            const websocket = new WebSocket(channel, []);
            websocketChannels[channel] = websocket;
            yield put(websocketChannelUpdated({ ...websocketChannels }));
            yield fork(websocketEventListenersSaga, websocket, channel);
            if (ping !== false) {
                yield call(subscriptionPingSubscribeSagas, channel);
            }
        } catch (error) {
            console.error('onOpenChannelSaga', error);
        }
    }
}

export function* onCloseChannelSaga(action: ReturnType<typeof websocketChannelClose>) {
    const { channel } = action.payload; 
    const websocketChannels = globalWebsocketChannels;
    if (websocketChannels[channel]) {
        websocketChannels[channel].close();
        delete websocketChannels[channel];
        yield put(websocketChannelUpdated({ ...websocketChannels }));
    }
}

export function* awaitOpenChannelSaga(channel: string) {
    const websocketChannels = globalWebsocketChannels;
    if (!websocketChannels[channel]) {
        yield put(websocketChannelOpen({ channel }));
    }
    yield call(waitForSaga, (state) => {
        const channels = getWebsocketChannels(state);
        const websocket = channels[channel];
        return websocket?.readyState === WebSocket.OPEN;
    });
}

export function* onSendChannelSaga(action: ReturnType<typeof websocketChannelSend>) {
    const { channel, message } = action.payload;
    yield call(awaitOpenChannelSaga, channel);
    yield call(waitForSaga, (state) => {
        const websocketChannels = getWebsocketChannels(state);
        return websocketChannels[channel]?.readyState === WebSocket.OPEN;
    });
    const websocketChannels: ReturnType<typeof getWebsocketChannels> = yield select(getWebsocketChannels);
    websocketChannels[channel]?.send(message);
    yield put(websocketChannelSended(action.payload));
}

export function* onWebSocketCloseEventSaga(action: ReturnType<typeof websocketEventClose>) {
    const { channel } = action.payload;
    const isAppVisibility = yield select(getIsAppVisibility);
    // если приложение видимо то открываем канал
    // нужно чтобы можно быо закрыть каналы при сворачивании приложения в onVisibilityChangeSaga
    if (isAppVisibility) {
        const state = yield select(getWebsocketReadyState(channel));
        if (state === WebSocket.CLOSING) {
            yield call(waitForSaga, (state: IMobileAppRootState) => {
                const status = getWebsocketReadyState(channel)(state);
                return status === WebSocket.CLOSED;
            });
        }

        if (state === WebSocket.CLOSED) {
            yield put(websocketChannelOpen({ channel }));
        }
    }

    // создаем событие setLiteTradeSocketState при закрытии канала
    const readyState = yield select(getWebsocketReadyStateNum);
    yield put(setLiteTradeSocketState(readyState));
}

export function* onWebSocketOpenEventSaga(action: ReturnType<typeof websocketEventOpen>) {
    // создаем событие setLiteTradeSocketState при открытии канала
    const readyState = yield select(getWebsocketReadyStateNum);
    yield put(setLiteTradeSocketState(readyState));
}


const prevReadyState: { [key: string]: number } = {};

export function* onWebsocketChannelReadyStateChangeSagas() {
    const readyState: { [channel: string]: WebSocket["readyState"] } = yield select(getWebsocketReadyState());
    let isChange = false;
    const channels = Object.keys(readyState);
    Object.keys(prevReadyState).forEach((channel) => {
        if (!channels.includes(channel)) {
            channels.push(channel);
        }
    });
    channels.forEach((channel) => {
        if (prevReadyState[channel] !== readyState[channel]) {
            isChange = true;
        }
        if (channel in readyState) {
            prevReadyState[channel] = readyState[channel];
        } else {
            delete prevReadyState[channel];
        }
    });
    if (isChange) {
        yield put(websocketChannelReadyStateChange({ ...readyState }));
    }
}